import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Truck, Check } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";
import { inr } from "@/lib/format";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

export function lockValidTill(item, lockMinutes) {
  if (!item.locked_at) return null;
  return new Date(item.locked_at).getTime() + lockMinutes * 60000;
}

export function LockLine({ item, className = "" }) {
  const { config } = useStore();
  const till = lockValidTill(item, config?.price_lock_minutes ?? 30);
  if (!till) return null;
  const expired = Date.now() > till;
  if (expired) {
    return <p className={`text-[10px] font-mono text-amber-700 ${className}`} data-testid={`lock-expired-${item.sku}`}>RATE LOCK EXPIRED — REFRESHES AT CHECKOUT</p>;
  }
  const time = new Date(till).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
  return <p className={`text-[10px] font-mono text-stone-500 ${className}`} data-testid={`lock-till-${item.sku}`}>PRICE LOCKED TILL {time}</p>;
}

// Build the reward ladder from config: free shipping threshold + any tiered offers.
function rewardTiers(config) {
  const tiers = [];
  const fst = Number(config?.free_shipping_threshold || 0);
  if (fst > 0) tiers.push({ threshold: fst, label: "free insured shipping" });
  for (const t of config?.tiered_offers || []) {
    const th = Number(t.threshold ?? t.min_subtotal ?? t.min ?? t.amount ?? 0);
    const label = t.label || t.reward || t.title || "";
    if (th > 0 && label) tiers.push({ threshold: th, label });
  }
  return tiers.sort((a, b) => a.threshold - b.threshold);
}

function RewardProgress({ subtotal }) {
  const { config } = useStore();
  const tiers = rewardTiers(config);
  if (!tiers.length) return null;
  const next = tiers.find((t) => subtotal < t.threshold);
  const top = tiers[tiers.length - 1];
  const pct = Math.min(100, (subtotal / (next ? next.threshold : top.threshold)) * 100);
  const unlocked = tiers.filter((t) => subtotal >= t.threshold);

  return (
    <div className="border border-gold/40 bg-ivory px-4 py-3 rounded-lg" data-testid="cart-reward-progress">
      {next ? (
        <p className="text-sm text-brand">
          <Truck size={15} className="inline mr-1.5 -mt-0.5" />
          Add <strong>{inr(next.threshold - subtotal)}</strong> more for <strong>{next.label}</strong>
        </p>
      ) : (
        <p className="text-sm text-emerald-700 flex items-center gap-1.5">
          <Check size={15} /> You've unlocked {unlocked.map((t) => t.label).join(" + ")}
        </p>
      )}
      <div className="h-1.5 bg-stone-200 mt-2 overflow-hidden rounded-full">
        <div className="h-full bg-brand transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid="cart-reward-bar" />
      </div>
    </div>
  );
}

function BestsellersRail({ onNavigate }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/bestsellers?limit=4").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-8 w-full" data-testid="cart-bestsellers">
      <p className="text-[11px] font-mono uppercase tracking-widest text-stone-500 mb-3 text-left">Bestsellers right now</p>
      <div className="grid grid-cols-2 gap-3">
        {items.map((p) => (
          <Link key={p.id} to={`/products/${p.slug}`} onClick={onNavigate}
            className="group border border-stone-200 bg-white rounded-lg overflow-hidden hover:border-brand transition-colors text-left"
            data-testid={`cart-bestseller-${p.slug}`}>
            <div className="aspect-square bg-ivory overflow-hidden">
              {(p.images || [])[0]
                ? <img src={p.images[0]} alt={p.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
                : <div className="h-full w-full grid place-items-center text-stone-300 font-mono text-[10px]">SIYAARA</div>}
            </div>
            <div className="p-2">
              <p className="text-xs font-medium line-clamp-1">{p.title}</p>
              <p className="text-xs font-semibold gold-num mt-0.5">{inr(p.price_min)}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0 bg-[#FDFCFA]" data-testid="cart-drawer">
        <SheetHeader className="px-5 py-4 border-b border-stone-200 bg-white">
          <SheetTitle className="font-heading flex items-center justify-between">
            Your Cart
            <span className="text-sm font-mono text-stone-500 font-normal">{items.length} item(s)</span>
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center text-center gap-3 pt-8" data-testid="cart-empty-state">
              <p className="text-stone-500 text-sm">Your cart is empty.</p>
              <Button variant="outline" onClick={() => { setDrawerOpen(false); navigate("/shop"); }} data-testid="cart-empty-shop-btn">
                Explore Jewellery
              </Button>
              <BestsellersRail onNavigate={() => setDrawerOpen(false)} />
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li key={item.key} className="flex gap-3 border border-stone-200 bg-white p-3" data-testid={`cart-item-${item.sku}`}>
                  <Link to={`/products/${item.slug}`} onClick={() => setDrawerOpen(false)} className="shrink-0">
                    {item.image
                      ? <img src={item.image} alt={item.title} className="h-20 w-20 object-cover bg-stone-50" />
                      : <div className="h-20 w-20 bg-[#FBF8F3] border border-stone-200 flex items-center justify-center" aria-hidden="true"><span className="font-heading text-brand/40">S</span></div>}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium leading-tight line-clamp-1">{item.title}</p>
                    <p className="text-xs font-mono text-stone-500 mt-0.5">{item.variant_label}{item.design ? ` · ${item.design}` : ""} · {item.purity}</p>
                    {item.personalisation?.summary && (
                      <p className="text-[11px] text-brand mt-0.5 line-clamp-1" data-testid={`cart-pers-${item.sku}`}>✦ {item.personalisation.summary}</p>
                    )}
                    <LockLine item={item} />
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-stone-200">
                        <button className="p-1.5 hover:bg-stone-100" onClick={() => updateQty(item.key, item.qty - 1)} data-testid={`cart-qty-minus-${item.sku}`}><Minus size={13} /></button>
                        <span className="px-2 text-sm font-mono w-8 text-center" data-testid={`cart-qty-${item.sku}`}>{item.qty}</span>
                        <button className="p-1.5 hover:bg-stone-100" onClick={() => updateQty(item.key, item.qty + 1)} data-testid={`cart-qty-plus-${item.sku}`}><Plus size={13} /></button>
                      </div>
                      <span className="font-heading font-semibold text-sm">{inr(item.locked_price * item.qty)}</span>
                    </div>
                  </div>
                  <button className="self-start text-stone-400 hover:text-red-600" onClick={() => removeItem(item.key)} data-testid={`cart-remove-${item.sku}`} aria-label="Remove">
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {items.length > 0 && (
          <div className="border-t border-stone-200 px-5 py-4 space-y-3 bg-white">
            <RewardProgress subtotal={subtotal} />
            <div className="flex justify-between font-heading font-semibold text-lg">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">{inr(subtotal)}</span>
            </div>
            <p className="text-[11px] text-stone-500">Inclusive of GST · Free insured shipping</p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => { setDrawerOpen(false); navigate("/cart"); }} data-testid="cart-view-btn">View Cart</Button>
              <Button className="bg-stone-900 hover:bg-brand text-white btn-press" onClick={() => { setDrawerOpen(false); navigate("/checkout"); }} data-testid="cart-checkout-btn">
                Checkout
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
